import {useDispatch, useSelector} from 'react-redux'
import {calcPrices} from 'app/lib/calcPrice'
import {addDays} from 'date-fns'
import {
	makeGuestsObj,
	updateOptions,
	normalizeExtras,
	calcExtraTypes,
	calcFirstBookableDate,
} from './helpers'
import {calcDueDates, fixDate, makePlainRange} from 'app/lib'
import {useEffect} from 'react'
import {change} from 'redux-form'
import {isEqual} from 'lodash-es'
import {useQueryParams} from 'app/hooks'
import {checkISOStringDate} from 'plugins/i18n'
import {formId} from './Reservation'
import {useTaxPlatform} from 'app/_server/queries/taxPlatforms'

const rentalFeesInit: Partial<RentalFees> = {
	adminFee: __CONFIG__.rentalFees.adminFee,
	insuranceRate: __CONFIG__.rentalFees.insuranceRate,
	depositRate: __CONFIG__.rentalFees.depositRate,
}

export const useParseDateRangeFromUrl = () => {
	const queryParams = useQueryParams()
	const start = queryParams.get('start')
	const end = queryParams.get('end')

	if (
		start &&
		end &&
		checkISOStringDate(start as DateISO) &&
		checkISOStringDate(end as DateISO)
	) {
		return makePlainRange(new Date(start as DateISO), new Date(end as DateISO))
	}
	return null
}

export const useInitFormValue = ({house}: {house?: House}) => {
	const dateRangeFromParams = useParseDateRangeFromUrl()

	const {extras: houseExtras, taxPlatformId} = house || {}
	const TPResult = useTaxPlatform({id: taxPlatformId!}, {skip: !taxPlatformId})

	if (!house) return null

	const taxPlatform = TPResult.data?.taxPlatform

	const firstAveDate = calcFirstBookableDate(house)
	if (!firstAveDate) throw new Error('No first available date')

	const initBeginDate = dateRangeFromParams?.start || fixDate(firstAveDate)
	const initEndDate =
		dateRangeFromParams?.end || fixDate(addDays(firstAveDate, 1))

	const initGuests: GuestsFormType = makeGuestsObj()

	const houseExtrasNorm = normalizeExtras(houseExtras!)

	const initSelectableExtrasValues = Object.fromEntries(
		houseExtrasNorm
			.filter(e => !e.required && !e.pet)
			.map(extra => {
				const {isRealPer} = calcExtraTypes(extra)
				return [extra.optionId, isRealPer ? extra.count : true]
			})
	)

	const initOptions = updateOptions(
		{
			...initGuests,
			beginDate: initBeginDate,
			endDate: initEndDate,
			house,
		},
		initSelectableExtrasValues
	)

	const initPrices = calcPrices(
		house,
		{
			beginDate: initBeginDate,
			endDate: initEndDate,
			options: initOptions,
			rentalFees: rentalFeesInit,
			hasInsurance: false,
			derived: true,
			...initGuests,
			hasTouristTax: !!taxPlatform,
		},
		taxPlatform
	)

	const initContract: ReservationContractFormType['contract'] = {
		...initGuests,
		beginDate: initBeginDate,
		clientId: '',
		createdThroughWebsite: true,
		endDate: initEndDate,
		hasInsurance: false,
		houseId: house.id,
		house,
		insuranceGuests: undefined,
		options: initOptions,
		petsNote: '',
		prices: initPrices,
		rentalFees: rentalFeesInit,
	}

	const initClient: ReservationContractFormType['client'] = {
		address: {countryId: 'c$BE'},
		name: {first: '', last: ''},
		tel: '',
		email: '',
		conditionsAccept: false,
		newsletterAccept: false,
		knowFrom: '',
		lang: 'en',
	}

	const initVals: ReservationContractFormType = {
		dateRange: makePlainRange(initBeginDate, initEndDate),
		selectableExtrasValues: initSelectableExtrasValues,
		client: initClient,
		contract: initContract,
		taxPlatform,
	}

	return initVals
}

export const useReservationFormState = () => {
	const dispatch = useDispatch()
	const formValue = useSelector(
		(state: any) => state.form?.[formId]?.values
	) as ReservationContractFormType

	const {contract, dateRange, selectableExtrasValues, taxPlatform} = formValue
	const {
		beginDate,
		dueDates,
		endDate,
		hasInsurance,
		house,
		options,
		prices,
		pets,
	} = contract || {}
	const {extras: houseExtras} = house || {}
	const guests = makeGuestsObj(contract)

	// Update insuranceGuests
	useEffect(() => {
		if (!hasInsurance) {
			dispatch(change(formId, `contract.insuranceGuests`, undefined))
		}
	}, [hasInsurance])

	// Update contract beginDate and endDate
	useEffect(() => {
		if (beginDate !== dateRange?.start) {
			dispatch(change(formId, `contract.beginDate`, dateRange?.start))
		}
		if (endDate !== dateRange?.end) {
			dispatch(change(formId, `contract.endDate`, dateRange?.end))
		}
	}, [dateRange])

	// Update pet extras selectableExtrasValues
	useEffect(() => {
		if (!house) return

		const newSelectable = {...selectableExtrasValues}
		// modify pet extras
		for (const {optionId} of houseExtras.filter(e => e.pet)) {
			newSelectable[optionId] = !!pets
		}

		if (!isEqual(newSelectable, selectableExtrasValues)) {
			dispatch(change(formId, `selectableExtrasValues`, newSelectable))
		}
	}, [pets])

	// Update options
	useEffect(() => {
		if (!house || !dateRange) return

		const newSelected = updateOptions(contract, selectableExtrasValues)
		if (!isEqual(newSelected, options)) {
			dispatch(change(formId, `contract.options`, newSelected))
		}
	}, [guests, selectableExtrasValues])

	// Update prices
	useEffect(() => {
		if (!house || !dateRange) return

		const newPrices = calcPrices(
			house,
			{
				...contract,
				derived: true,
				hasTouristTax: !!taxPlatform,
			},
			taxPlatform
		)

		if (!isEqual(prices, newPrices)) {
			dispatch(change(formId, `contract.prices`, newPrices))
		}
	}, [dateRange, contract])

	// Update dueDates
	useEffect(() => {
		if (!house || !dateRange || !prices) return
		const newDueDates = calcDueDates({
			contractDate: fixDate(new Date()),
			beginDate: dateRange.start,
			prices,
		})
		if (!isEqual(newDueDates, dueDates)) {
			dispatch(change(formId, `contract.dueDates`, newDueDates))
		}
	}, [dateRange, prices])

	return {formValue}
}
